import React from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// Barre de progression réutilisée pour habitudes / compétences / KPI
const WidgetProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#0d4f86',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
  },
}))

const panelStyle = {
  flex: 1,
  minWidth: 220,
  // LAP — aligné sur les cartes de la synthèse (dégradé + bordure subtile)
  background: 'linear-gradient(180deg, #0a4173 0%, #083b67 100%)',
  border: '1px solid rgba(255,255,255,0.08)',
  padding: '16px',
  borderRadius: '20px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
  color: 'white',
}

// Widgets dédiés au suivi par type de tâche (habitudes, compétences, KPI).
// Reçoit la liste complète des tâches ; chaque section ne s'affiche que si
// au moins une tâche du type correspondant existe.
export default function TypeWidgets({ tasks = [] }) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const habitudes = tasks.filter((v) => v.TypeTache === 'habitude')
  const competences = tasks.filter((v) => v.TypeTache === 'competence')
  const kpis = tasks.filter((v) => v.TypeTache === 'kpi')

  if (habitudes.length === 0 && competences.length === 0 && kpis.length === 0) {
    return null
  }

  const Row = ({ label, value, percent }) => (
    <Box sx={{ marginBottom: '10px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Box>
        <Box component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap', marginLeft: '8px' }}>
          {value}
        </Box>
      </Box>
      <WidgetProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, percent || 0))}
      />
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2,
        padding: isMobile ? '10px 5px 0' : '10px 25px 0',
      }}
    >
      {habitudes.length > 0 && (
        <Box sx={panelStyle}>
          <Typography variant="h6" sx={{ color: 'white', marginBottom: '10px' }}>
            {t('mesToDo.widgetHabits')}
          </Typography>
          {habitudes.map((v) => (
            <Row
              key={v.id}
              label={v.Nom}
              value={`${t('mesToDo.streakShort')} ${v.habit_streak ?? 0} · ${Math.round(
                v.habit_success_rate ?? 0,
              )}%`}
              percent={v.habit_success_rate ?? 0}
            />
          ))}
        </Box>
      )}

      {competences.length > 0 && (
        <Box sx={panelStyle}>
          <Typography variant="h6" sx={{ color: 'white', marginBottom: '10px' }}>
            {t('mesToDo.widgetSkills')}
          </Typography>
          {competences.map((v) => (
            <Row
              key={v.id}
              label={v.Nom}
              value={`${v.NiveauActuel ?? 0}/${v.NiveauCible ?? 5}`}
              percent={v.competence_progress ?? 0}
            />
          ))}
        </Box>
      )}

      {kpis.length > 0 && (
        <Box sx={panelStyle}>
          <Typography variant="h6" sx={{ color: 'white', marginBottom: '10px' }}>
            {t('mesToDo.widgetKpis')}
          </Typography>
          {kpis.map((v) => (
            <Row
              key={v.id}
              label={v.Nom}
              value={`${v.KpiActuel ?? 0}${v.KpiCible ? ` / ${v.KpiCible}` : ''} ${
                v.KpiUnite || ''
              }`}
              percent={v.kpi_progress ?? 0}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
